import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/chords/Icon';
import type { MenuItemState } from '~/chords/KebabMenu/components/KebabMenuItem/KebabMenuItem';
import { KebabMenu } from '~/chords/KebabMenu/KebabMenu';
import { cn } from '~/chords/lib/cn';

export interface SelectedItemRowSecondaryAction {
  label: string;
  onAction: () => void;
  state?: MenuItemState;
  availableInReadOnly?: boolean;
}

export interface SelectedItemRowProps {
  id: string;
  label: string;
  imageSrc?: string;
  renderContent?: () => ReactNode;
  secondaryActions?: SelectedItemRowSecondaryAction[];
  onRemove: () => void;
  removeLabel?: string;
  disabled?: boolean;
}

export function SelectedItemRow({
  id,
  label,
  imageSrc,
  renderContent,
  secondaryActions = [],
  onRemove,
  removeLabel,
  disabled = false,
}: SelectedItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const { t } = useTranslation('chords');

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const availableActions = disabled
    ? secondaryActions.filter((action) => action.availableInReadOnly)
    : secondaryActions;
  const showActionsMenu = !disabled || availableActions.length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex w-full items-center gap-sm rounded-sm px-md py-sm bg-level-04 border-emphasis-default',
        { 'relative z-10': isDragging },
      )}
    >
      {!disabled ? (
        <button
          type="button"
          className="flex shrink-0 cursor-grab items-center justify-center text-disabled active:cursor-grabbing"
          aria-label={t('multiSelect.dragToReorder', { label })}
          {...attributes}
          {...listeners}
        >
          <Icon type="grip-vertical" variant="solid" size="sm" aria-hidden />
        </button>
      ) : null}

      <div className="flex min-w-0 flex-1 items-center gap-sm">
        {renderContent ? (
          renderContent()
        ) : (
          <>
            {imageSrc ? (
              <img
                src={imageSrc}
                alt=""
                className="size-xl shrink-0 rounded-full object-cover"
              />
            ) : null}
            <span className="truncate text-body-sm text-white">{label}</span>
          </>
        )}
      </div>

      {showActionsMenu ? (
        <div className="shrink-0">
          <KebabMenu size="small" iconVariant="regular">
            {!disabled ? (
              <KebabMenu.Item type="button" onClick={onRemove} state="error">
                {removeLabel ?? t('multiSelect.remove')}
              </KebabMenu.Item>
            ) : null}
            {availableActions.map((action) => (
              <KebabMenu.Item
                key={action.label}
                type="button"
                onClick={action.onAction}
                state={action.state}
              >
                {action.label}
              </KebabMenu.Item>
            ))}
          </KebabMenu>
        </div>
      ) : null}
    </div>
  );
}

SelectedItemRow.displayName = 'SelectedItemRow';
